
import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  XCircle,
  BrainCircuit,
  Trophy,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BookOpenCheck,
  Brain,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link, useParams } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { QuizResultsData, Question, categorizeStrength, MicroSkillAnalysis } from "@/types/quiz";
import CognitiveLoadTracker from "./CognitiveLoadTracker";
import CognitiveFatigueReport from "./CognitiveFatigueReport";
import MicroSkillAnalytics from "./MicroSkillAnalytics";
import { analyzeMicroSkills } from "@/types/quiz";
import PersonalKnowledgeGraph from "./PersonalKnowledgeGraph";
import LanguageExplanation from "./LanguageExplanation";

interface MockTestResultsProps {
  resultsData: QuizResultsData | null;
  questionTimings: Record<string, number>;
  navigationPatterns: Array<{time: number, from: number, to: number}>;
}

const MockTestResults: React.FC<MockTestResultsProps> = ({ resultsData, questionTimings, navigationPatterns }) => {
  const [selectedQuestionIndex, setSelectedQuestionIndex] = React.useState<number | null>(null);
  const { resultId } = useParams();
  const [loadedResults, setLoadedResults] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (resultId) {
      try {
        console.log("Attempting to load results with ID:", resultId);
        const storedResults = localStorage.getItem(`mockTestResult_${resultId}`);
        
        if (storedResults) {
          console.log("Found stored results:", JSON.parse(storedResults));
          setLoadedResults(JSON.parse(storedResults));
        } else {
          console.error("No results found with ID:", resultId);
        }
      } catch (error) {
        console.error("Error loading results:", error);
      }
      setLoading(false);
    }
  }, [resultId]);

  const effectiveResultsData = resultsData || (loadedResults ? {
    questions: loadedResults.testConfig.questions,
    answers: Object.values(loadedResults.submittedAnswers),
    score: loadedResults.score,
    total_questions: loadedResults.testConfig.questions.length,
    percentage: loadedResults.percentage,
    subject: loadedResults.testConfig.subject,
    category: loadedResults.testConfig.category
  } : null);
  
  const effectiveQuestionTimings = questionTimings || (loadedResults?.cognitiveData?.questionTimings || {});
  const effectiveNavigationPatterns = navigationPatterns || (loadedResults?.cognitiveData?.navigationPatterns || []);

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading test results...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!effectiveResultsData && !loadedResults) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-gray-500 italic">
            <AlertTriangle className="h-4 w-4" />
            <p>No results data available for this test attempt.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  console.log("Using effective results data:", effectiveResultsData);
  console.log("Using loaded results:", loadedResults);

  if (loadedResults && !effectiveResultsData) {
    console.error("Loaded results exist but could not be parsed into the expected format");
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p>The results data has an unexpected format.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/mock-test-generator">Back to Test Generator</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const questions = effectiveResultsData?.questions || [];
  const answers = effectiveResultsData?.answers || [];
  const score = effectiveResultsData?.score || 0;
  const total_questions = effectiveResultsData?.total_questions || 0;
  const percentage = effectiveResultsData?.percentage || 0;
  const subject = effectiveResultsData?.subject || loadedResults?.testConfig?.subject || '';
  const category = effectiveResultsData?.category || loadedResults?.testConfig?.category || '';

  const isPassing = percentage >= 60;

  const extractRealMicroSkills = (questions: Question[]): Question[] => {
    return questions.map(question => {
      const questionText = question.question.toLowerCase();
      const questionCategory = question.category?.toLowerCase() || '';
      const questionSubject = question.subject?.toLowerCase() || '';
      const extractedSkills: string[] = [];
      
      if (questionText.includes('force') || questionText.includes('motion') || questionText.includes('newton')) {
        extractedSkills.push('Classical Mechanics');
      }
      if (questionText.includes('electric') || questionText.includes('magnet') || questionText.includes('charge')) {
        extractedSkills.push('Electromagnetism');
      }
      if (questionText.includes('therm') || questionText.includes('heat') || questionText.includes('temperature')) {
        extractedSkills.push('Thermodynamics');
      }
      
      if (questionText.includes('equation') || questionText.includes('algebra') || questionText.includes('solve')) {
        extractedSkills.push('Algebra');
      }
      if (questionText.includes('calcul') || questionText.includes('derivative') || questionText.includes('integral')) {
        extractedSkills.push('Calculus');
      }
      
      if (questionText.includes('president') || questionText.includes('minister') || questionText.includes('government')) {
        extractedSkills.push('Politics');
      }
      if (questionText.includes('country') || questionText.includes('capital') || questionText.includes('continent')) {
        extractedSkills.push('Geography');
      }
      if (questionText.includes('history') || questionText.includes('war') || questionText.includes('revolution')) {
        extractedSkills.push('Historical Events');
      }
      
      if (questionText.includes('science') || questionText.includes('technology') || 
          questionCategory.includes('science') || questionSubject.includes('science')) {
        extractedSkills.push('Science & Technology');
      }
      
      if (questionText.includes('current') || questionText.includes('recent') || 
          questionCategory.includes('current affairs') || questionSubject.includes('current affairs')) {
        extractedSkills.push('Current Affairs');
      }
      
      if (questionText.includes('person') || questionText.includes('famous') || 
          questionText.includes('scientist') || questionText.includes('leader')) {
        extractedSkills.push('Famous Personalities');
      }
      
      if (questionText.includes('organization') || questionText.includes('international') || 
          questionText.includes('un') || questionText.includes('nato')) {
        extractedSkills.push('International Organizations');
      }
      
      if (extractedSkills.length === 0) {
        if (questionCategory) {
          extractedSkills.push(questionCategory);
        } else if (questionSubject) {
          extractedSkills.push(questionSubject);
        } else {
          extractedSkills.push('General Knowledge');
        }
      }
      
      return {
        ...question,
        microSkills: extractedSkills
      };
    });
  };
  
  const questionsWithMicroSkills = questions.length > 0 
    ? extractRealMicroSkills(questions)
    : [];
    
  const microSkillsAnalysis = questions.length > 0 
    ? analyzeMicroSkills(questionsWithMicroSkills, answers)
    : [];

  const totalEarnedPoints = score;
  const totalPossiblePoints = questions.reduce((total, question) => {
    const marks = typeof question.marks === 'number' 
      ? question.marks 
      : typeof question.marks === 'string'
        ? parseFloat(question.marks) || 1
        : 1;
    return total + marks;
  }, 0);
  
  const incorrectAnswers = questions.filter((q, idx) => answers[idx] !== undefined && answers[idx] !== null && answers[idx] !== q.correctanswer).length;
  const pointsLostToNegativeMarking = questions.reduce((total, question, index) => {
    if (index < answers.length && answers[index] !== null && answers[index] !== question.correctanswer) {
      const negativeMarks = typeof question.negative_marks === 'number' ? 
                          question.negative_marks : 
                          typeof question.negative_marks === 'string' ? 
                          parseFloat(question.negative_marks) || 0.25 : 
                          0.25;
      return total + negativeMarks;
    }
    return total;
  }, 0);

  // Fixed formatMarks to ensure it properly handles all numeric values
  const formatMarks = (value: number): string => {
    // Check if it's a whole number (integer)
    if (Math.floor(value) === value) {
      return value.toString();
    }
    // For decimal values, limit to 2 decimal places and remove trailing zeros
    return parseFloat(value.toFixed(2)).toString();
  };

  // Fixed getQuestionMarks to ensure it correctly parses and formats marks
  const getQuestionMarks = (question: Question, isCorrect: boolean): string => {
    let marksValue: number = 1; // Default value
    
    if (typeof question.marks === 'number') {
      marksValue = question.marks;
    } else if (typeof question.marks === 'string' && question.marks) {
      const parsedValue = parseFloat(question.marks);
      if (!isNaN(parsedValue)) {
        marksValue = parsedValue;
      }
    }
      
    if (!isCorrect) {
      let negativeMarks: number = 0.25; // Default value
      
      if (typeof question.negative_marks === 'number') {
        negativeMarks = question.negative_marks;
      } else if (typeof question.negative_marks === 'string' && question.negative_marks) {
        const parsedValue = parseFloat(question.negative_marks);
        if (!isNaN(parsedValue)) {
          negativeMarks = parsedValue;
        }
      }
      
      return `-${formatMarks(negativeMarks)}`;
    }
    
    return `+${formatMarks(marksValue)}`;
  };

  // Fixed timingValues to properly handle non-numeric values
  const timingValues: number[] = Object.values(effectiveQuestionTimings)
    .filter(time => time !== undefined && time !== null)
    .map(time => typeof time === 'number' ? time : 0);
    
  // Fixed averageTimePerQuestion calculation
  const averageTimePerQuestion = timingValues.length > 0 
    ? timingValues.reduce((acc, time) => acc + time, 0) / timingValues.length
    : 0;

  return (
    <div className="container mx-auto mt-8">
      <Card className="mb-6 overflow-hidden border-primary/10">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-4">
            <BrainCircuit className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl font-bold">Mock Test Results</CardTitle>
          </div>
          <Badge variant="secondary">
            {isPassing ? "Passed" : "Failed"}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-3/4">
                <CardDescription className="text-gray-500">
                  You scored {totalEarnedPoints.toFixed(2)} out of {totalPossiblePoints.toFixed(2)} possible points.
                  {pointsLostToNegativeMarking > 0 && 
                    ` (Lost ${pointsLostToNegativeMarking.toFixed(2)} to negative marking)`}
                </CardDescription>
                <Progress value={percentage} className="h-4 mt-2" />
                <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="w-1/4 text-center">
                <div className="text-3xl font-bold">{percentage}%</div>
                <div className="text-sm text-gray-500">Overall Score</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                {isPassing ? (
                  <CheckCircle2 className="text-green-500 h-5 w-5" />
                ) : (
                  <XCircle className="text-red-500 h-5 w-5" />
                )}
                <p className="text-sm">
                  You {isPassing ? "passed" : "failed"} this mock test.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Brain className="text-blue-500 h-5 w-5" />
                <p className="text-sm">
                  Subject: {subject || "General Knowledge"}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <BookOpenCheck className="text-orange-500 h-5 w-5" />
                <p className="text-sm">
                  Category: {category || "All"}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {percentage > 70 ? (
                  <TrendingUp className="text-green-500 h-5 w-5" />
                ) : (
                  <TrendingDown className="text-red-500 h-5 w-5" />
                )}
                <p className="text-sm">
                  Performance: {percentage > 70 ? "Improving" : "Needs Improvement"}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="mb-2 font-semibold">Key Statistics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Trophy className="text-yellow-500 h-5 w-5" />
                  <p className="text-sm">Correct Answers: {questions.filter((q, idx) => answers[idx] === q.correctanswer).length}/{total_questions}</p>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-orange-500 h-5 w-5" />
                  <p className="text-sm">
                    Negative Marks: -{pointsLostToNegativeMarking.toFixed(2)} points
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <BrainCircuit className="text-purple-500 h-5 w-5" />
                  <p className="text-sm">Total Questions: {total_questions}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <Button asChild variant="outline">
                <Link to="/dashboard">Back to Dashboard</Link>
              </Button>
              <Button asChild>
                <Link to="/mock-test">Retake Mock Test</Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {Object.keys(effectiveQuestionTimings).length > 0 && (
        <CognitiveLoadTracker 
          questionTimings={effectiveQuestionTimings}
          navigationPatterns={effectiveNavigationPatterns}
          currentQuestionIndex={0}
          totalQuestions={total_questions}
        />
      )}
      
      {Object.keys(effectiveQuestionTimings).length > 0 && (
        <CognitiveFatigueReport cognitiveData={{
          questionTimings: effectiveQuestionTimings,
          navigationPatterns: effectiveNavigationPatterns,
          averageTimePerQuestion: averageTimePerQuestion,
          fatigueZones: []
        }} />
      )}
      
      {questions.length > 0 && (
        <MicroSkillAnalytics microSkillsAnalysis={microSkillsAnalysis} />
      )}

      {questions.length > 0 && (
        <PersonalKnowledgeGraph 
          microSkillsAnalysis={microSkillsAnalysis} 
          subject={subject || category || null} 
        />
      )}
      
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl">
            Question Review
          </CardTitle>
        </CardHeader>
        <CardContent>
          {questions.length > 0 ? (
            <>
              <div className="grid grid-cols-5 gap-2 mb-4">
                {questions.map((question, index) => (
                  <Button 
                    key={index}
                    variant={selectedQuestionIndex === index ? "default" : "outline"}
                    className={cn(
                      "w-full h-10",
                      answers[index] === questions[index].correctanswer 
                        ? "border-green-500" 
                        : "border-red-500"
                    )}
                    onClick={() => setSelectedQuestionIndex(index)}
                  >
                    {index + 1}
                  </Button>
                ))}
              </div>
              
              {selectedQuestionIndex !== null && questions[selectedQuestionIndex] && (
                <div className="border rounded-md p-4 mt-4">
                  <h3 className="font-semibold text-lg mb-2">Question {selectedQuestionIndex + 1}</h3>
                  <p className="mb-4">{questions[selectedQuestionIndex].question}</p>
                  
                  <div className="space-y-2 mb-4">
                    {questions[selectedQuestionIndex].options.map((option, optIndex) => {
                      const isCorrect = optIndex === questions[selectedQuestionIndex].correctanswer;
                      const isSelected = optIndex === answers[selectedQuestionIndex];
                      const currentQuestion = questions[selectedQuestionIndex];
                      
                      let marksDisplay = null;
                      if (isSelected) {
                        marksDisplay = getQuestionMarks(currentQuestion, isCorrect);
                      }
                      
                      return (
                        <div 
                          key={optIndex}
                          className={cn(
                            "p-3 rounded-md border",
                            optIndex === questions[selectedQuestionIndex].correctanswer 
                              ? "bg-green-50 border-green-200" 
                              : optIndex === answers[selectedQuestionIndex]
                                ? "bg-red-50 border-red-200"
                                : "bg-gray-50 border-gray-200"
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start">
                              <div className="mr-2 mt-0.5">
                                {optIndex === questions[selectedQuestionIndex].correctanswer ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                                ) : optIndex === answers[selectedQuestionIndex] ? (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                ) : (
                                  <div className="h-5 w-5 rounded-full border border-gray-300 flex items-center justify-center">
                                    {String.fromCharCode(65 + optIndex)}
                                  </div>
                                )}
                              </div>
                              <div>{option}</div>
                            </div>
                            {marksDisplay && (
                              <div className="ml-2 text-sm font-medium">
                                <span className={isCorrect ? "text-green-600" : "text-red-600"}>
                                  {marksDisplay} marks
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <LanguageExplanation 
                    question={questions[selectedQuestionIndex]} 
                    questionNumber={selectedQuestionIndex + 1}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-6">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p>No question data available for review.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MockTestResults;
